import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { marketing, fmtMoney, fmtDate } from "@/site/api";
import { track } from "@/site/analytics";
import { onQuote, clearQuote } from "@/site/quote";

const ROLE_LABEL = { dealdesk: "Deal desk", leadership: "Leadership", admin: "Admin", am: "Account manager" };
const TERMS = [12, 18, 24, 36, 60];
const DEFAULT_START = "2026-01-01";

// Inclusive day count between two ISO dates (UTC, no DST drift).
const daysBetween = (a, b) => {
  const [y1, m1, d1] = a.split("-").map(Number); const [y2, m2, d2] = b.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1;
};
// A billing period shorter than a full cycle is a stub the server prorates by days.
const FULL_DAYS = { annual_advance: 365, quarterly_advance: 89 };
const isStub = (b, bf) => bf in FULL_DAYS && daysBetween(b.period_start, b.period_end) < FULL_DAYS[bf];
const termLabel = (m) => (m % 12 === 0 ? `${m / 12} year${m === 12 ? "" : "s"}` : `${m} months`);

/**
 * Deal calculator. Every number comes from the backend's compute_order_form via
 * /api/marketing/calc — nothing is computed here. That is deliberate: the site
 * must never show a figure the product would not.
 */
export default function Calculator() {
  const [cat, setCat] = useState(null);
  const [lines, setLines] = useState([{ sku: "CORE-BASE", quantity: 1, discount_pct: 10 }, { sku: "ADDON-ANALYTICS", quantity: 1, discount_pct: 0 }]);
  // Set when the whitespace grid hands us an account (click-to-quote).
  const [ctx, setCtx] = useState(null);

  useEffect(() => onQuote((q) => {
    if (!q?.lines?.length) return;
    setLines(q.lines.map((l) => ({ sku: l.sku, quantity: Math.max(1, l.quantity || 1), discount_pct: 0 })));
    setCtx(q);
    track("calc_prefilled", { account: q.account, source: q.source, modules: q.lines.length });
  }), []);
  const clearCtx = () => { setCtx(null); clearQuote(); };
  const [term, setTerm] = useState(18);
  const [start, setStart] = useState(DEFAULT_START);
  const [currency, setCurrency] = useState("USD");
  const [bf, setBf] = useState("annual_advance");
  const [uplift, setUplift] = useState(0);
  const [role, setRole] = useState("am");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  const firstCalc = useRef(true);

  useEffect(() => { marketing.catalog().then(setCat).catch(() => setErr("Catalog unavailable.")); }, []);

  const body = useMemo(() => ({ lines, term_months: term, currency, billing_frequency: bf, annual_uplift_pct: uplift, submitter_role: role, start_date: /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : DEFAULT_START }), [lines, term, start, currency, bf, uplift, role]);

  useEffect(() => {
    if (!cat) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true); setErr("");
      try { setOut(await marketing.calc(body)); if (!firstCalc.current) track("calc_change", { term, currency, lines: lines.length }); firstCalc.current = false; }
      catch (e) { setErr(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not price this deal."); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [body, cat]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (i, k, v) => setLines((ls) => ls.map((l, n) => (n === i ? { ...l, [k]: v } : l)));
  const add = () => { if (!cat) return; const used = new Set(lines.map((l) => l.sku)); const next = cat.products.find((p) => !used.has(p.sku)) || cat.products[0]; setLines((ls) => [...ls, { sku: next.sku, quantity: 1, discount_pct: 0 }]); };
  const rm = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, n) => n !== i) : ls));

  const c = out?.computation;
  const ap = out?.approval;
  const canSign = out?.can_sign;
  // The KPI row above already shows the blended discount number, so trim a
  // leading "Blended discount X%" restating it out of the reason text here --
  // this is a frontend-only display tweak. The backend string itself (also
  // what a real deal desk reviewer sees in the product's approval queue) is
  // untouched, since there the number isn't already sitting in a KPI above it.
  const stripDiscountPrefix = (s) => {
    if (!s) return s;
    const rest = s.replace(/^Blended discount [\d.]+%\s*/i, "");
    return rest.charAt(0).toUpperCase() + rest.slice(1);
  };
  const gov = !ap ? null : ap.needs_approval ? { cls: ap.approver_role === "leadership" || ap.approver_role === "admin" ? "lead" : "desk", pill: `Needs ${ROLE_LABEL[ap.approver_role] || ap.approver_role}`, why: ap.reasons?.length ? ap.reasons.map((r) => stripDiscountPrefix(r.label || r)).join(" · ") : stripDiscountPrefix(ap.summary) || "Over the discount threshold or beyond signing authority." }
    : { cls: "ok", pill: "In policy — auto-clears", why: `Within the ${cat?.policy?.approval_required_above ?? 20}% discount threshold${canSign ? " and within your signing authority." : "."}` };

  return (
    <div className="calc" id="calculator-widget" data-testid="calculator">
      <form className="calc__form" onSubmit={(e) => e.preventDefault()} aria-label="Deal calculator inputs">
        <div>
          <div className="eyebrow">Build a deal</div>
          <div className="field" style={{ marginTop: 6 }}><span className="hint">Pick modules, seats and a discount.</span></div>
        </div>
        {ctx && (
          <div className="calc__ctx" role="status" data-testid="calc-context">
            <div>
              <div className="calc__ctx-k">Quoting from the whitespace grid</div>
              <div className="calc__ctx-v" data-testid="calc-context-account">{ctx.account} <small>· {ctx.seats} seats · {ctx.lines.map((l) => l.module).join(", ")}</small></div>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={clearCtx} data-testid="calc-context-clear">Clear</button>
          </div>
        )}
        {lines.map((l, i) => {
          const p = cat?.products.find((x) => x.sku === l.sku);
          return (
            <div className="line" key={i} data-testid={`calc-line-${i}`}>
              <div className="field"><label htmlFor={`sku-${i}`}>Module</label>
                <select id={`sku-${i}`} className="select" value={l.sku} onChange={(e) => upd(i, "sku", e.target.value)}>
                  {(cat?.products || []).map((x) => <option key={x.sku} value={x.sku}>{x.name} — {fmtMoney(x.list_price)}{x.pricing_unit === "per_user" ? "/seat" : ""}</option>)}
                </select></div>
              <div className="field"><label htmlFor={`qty-${i}`}>{p?.pricing_unit === "per_user" ? "Seats" : "Qty"}</label>
                <input id={`qty-${i}`} className="input num" type="number" min="1" max="100000" value={l.quantity} onChange={(e) => upd(i, "quantity", Math.max(1, parseInt(e.target.value || "1", 10)))} /></div>
              <div className="field"><label htmlFor={`disc-${i}`}>Discount <span className="mono" style={{ color: "var(--faint)" }}>{l.discount_pct}%</span></label>
                <input id={`disc-${i}`} className="range" type="range" min="0" max="60" step="1" value={l.discount_pct} onChange={(e) => upd(i, "discount_pct", Number(e.target.value))} data-testid={`calc-discount-${i}`} /></div>
              <button type="button" className="line__rm" onClick={() => rm(i)} aria-label="Remove line" disabled={lines.length === 1}>✕</button>
            </div>
          );
        })}
        <div><button type="button" className="btn btn--ghost btn--sm" onClick={add} disabled={!cat || lines.length >= 6} data-testid="calc-add-line">+ Add module</button></div>
        <div className="field">
          <label id="term-label">Term <span className="mono" style={{ color: "var(--faint)" }}>{termLabel(term)}</span></label>
          <div className="seg" role="radiogroup" aria-labelledby="term-label" data-testid="calc-term">
            {TERMS.map((m) => (
              <button key={m} type="button" role="radio" aria-checked={term === m} className={`seg__btn ${term === m ? "on" : ""}`} onClick={() => setTerm(m)} data-testid={`calc-term-${m}`}>
                {m}<small>mo</small>
              </button>
            ))}
          </div>
          <span className="hint">18 months is not whole years — watch the final period get prorated by days, exactly as the product bills it.</span>
        </div>
        <div className="line" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
          <div className="field"><label htmlFor="start">Start date</label>
            <input id="start" className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="calc-start" /></div>
          <div className="field"><label htmlFor="cur">Currency</label>
            <select id="cur" className="select" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="calc-currency"><option>USD</option><option>EUR</option><option>GBP</option></select></div>
          <div className="field"><label htmlFor="bf">Billing</label>
            <select id="bf" className="select" value={bf} onChange={(e) => setBf(e.target.value)} data-testid="calc-billing"><option value="annual_advance">Annual in advance</option><option value="quarterly_advance">Quarterly</option><option value="upfront">Upfront</option></select></div>
        </div>
        <div className="line" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="field"><label htmlFor="uplift">Annual uplift <span className="mono" style={{ color: "var(--faint)" }}>{uplift}%</span></label>
            <input id="uplift" className="range" type="range" min="0" max="15" step="1" value={uplift} onChange={(e) => setUplift(Number(e.target.value))} /></div>
          <div className="field"><label htmlFor="role">Submitted by</label>
            <select id="role" className="select" value={role} onChange={(e) => setRole(e.target.value)} data-testid="calc-role"><option value="am">Account manager</option><option value="dealdesk">Deal desk</option><option value="leadership">Leadership</option></select></div>
        </div>
      </form>

      <div className="calc__out" aria-live="polite" data-testid="calc-output">
        <div className="eyebrow" style={{ color: "#d9b45b" }}>What the product would say</div>
        {err && <div className="notice" role="alert">{err}</div>}
        {c ? (
          <>
            <div className="calc__kpis">
              <div className="kpi"><div className="k">Annual (ARR)</div><div className="v num" data-testid="calc-arr">{fmtMoney(c.arr, currency)}</div><div className="s">{currency !== "USD" ? `${fmtMoney(c.arr_usd)} USD` : "entry ARR"}</div></div>
              <div className="kpi"><div className="k">Total contract</div><div className="v num" data-testid="calc-tcv">{fmtMoney(c.tcv, currency)}</div><div className="s" data-testid="calc-term-dates">{termLabel(term)} · {fmtDate(out.order_form.start_date)} → {fmtDate(out.order_form.end_date)}</div></div>
              <div className="kpi"><div className="k">Blended discount</div><div className="v num" data-testid="calc-discount">{c.blended_discount_pct.toFixed(1)}%</div><div className="s">vs catalog list</div></div>
            </div>
            {gov && (
              <div className="gov" data-testid="calc-governance">
                <div className="gov__title"><span className={`gov__pill gov__pill--${gov.cls}`} data-testid="calc-gov-pill">{gov.pill}</span>{ap.needs_approval ? "Routed for a decision" : "No human needed"}</div>
                <div className="gov__why">{gov.why}</div>
              </div>
            )}
            <table className="sched" data-testid="calc-schedule">
              <thead><tr><th>Billing schedule</th><th>Period</th><th className="r">Amount</th></tr></thead>
              <tbody>
                {c.billing_schedule.slice(0, 8).map((b, n) => {
                  const stub = isStub(b, bf);
                  return (
                    <tr key={n} className={stub ? "stub" : ""} data-testid={stub ? "calc-stub-row" : undefined}>
                      <td>#{n + 1}</td>
                      <td>{fmtDate(b.period_start)} → {fmtDate(b.period_end)}{stub && <span className="sched__tag">{daysBetween(b.period_start, b.period_end)} days · prorated</span>}</td>
                      <td className="r num">{fmtMoney(b.amount, currency, 2)}</td>
                    </tr>
                  );
                })}
                {c.billing_schedule.length > 8 && <tr><td colSpan={3} style={{ color: "#a9a293" }}>… {c.billing_schedule.length - 8} more installments</td></tr>}
                <tr className="total"><td colSpan={2}>Total contract value</td><td className="r num">{fmtMoney(c.tcv, currency, 2)}</td></tr>
              </tbody>
            </table>
            {c.billing_schedule.some((b) => isStub(b, bf)) && (
              <div className="calc__note" data-testid="calc-proration-note">
                The final period is shorter than a full cycle, so it is billed pro rata by day — the total is not simply ARR × years. This is the server's number; nothing on this page re-computes it.
              </div>
            )}
            <div className="calc__src">{busy ? "recalculating…" : `source: ${out.source} — the product's own pricing function, not a copy`}</div>
            <Link to="/pilot" className="btn btn--primary" onClick={() => track("cta_click", { where: "calculator" })} data-testid="calc-cta">Price your real book →</Link>
          </>
        ) : !err && <div style={{ color: "#a9a293" }} data-testid="calc-loading">Pricing…</div>}
      </div>
    </div>
  );
}
